import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Table,
  Space,
  Typography,
  Modal,
  Form,
  Input,
  message,
  Tooltip,
  Popconfirm,
  Tag,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  SearchOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import {
  useAppConfigTemplates,
  useDeleteAppConfigTemplateMutation,
  useWorkerTemplateNameMap,
} from "../hooks/useAppsQueries";
import { apiService } from "../services/api";
import { parseJsonArray } from "../helpers/helpers.app";
import type { AppConfigTemplate } from "../types/api";

const { Title, Text } = Typography;

export const Presets: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const presetsQuery = useAppConfigTemplates();
  const presets = presetsQuery.data ?? [];
  const deleteMutation = useDeleteAppConfigTemplateMutation();
  const templateNameById = useWorkerTemplateNameMap(
    presets.map((p) => p.templateId),
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingPreset, setEditingPreset] = useState<AppConfigTemplate | null>(null);
  const [editForm] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const filteredPresets = presets.filter((p) => {
    const q = searchTerm.toLowerCase();
    return (
      p.name.toLowerCase().includes(q) ||
      (p.description || "").toLowerCase().includes(q) ||
      (p.templateId || "").toLowerCase().includes(q) ||
      (p.greyType || "").toLowerCase().includes(q)
    );
  });

  const handleEdit = (preset: AppConfigTemplate) => {
    setEditingPreset(preset);
    editForm.setFieldsValue({
      name: preset.name,
      description: preset.description || "",
    });
    setEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingPreset) return;
    try {
      setSaving(true);
      const values = await editForm.validateFields();
      await apiService.updateAppConfigTemplate(editingPreset.id, values);
      message.success("Пресет оновлено");
      setEditModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ["appConfigTemplates"] });
    } catch {
      message.error("Помилка оновлення пресету");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteMutation.mutateAsync(id);
      message.success("Пресет видалено");
    } catch {
      message.error("Помилка видалення пресету");
    }
  };

  const columns = [
    {
      title: "Назва",
      dataIndex: "name",
      key: "name",
      render: (_: string, record: AppConfigTemplate) => (
        <div>
          <Text strong>{record.name}</Text>
          {record.description && (
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {record.description}
              </Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Шаблон",
      dataIndex: "templateId",
      key: "templateId",
      width: 200,
      render: (val: string) => {
        if (!val) return <Text type="secondary">—</Text>;
        const name = templateNameById.get(val);
        return (
          <Tooltip title={val}>
            <Tag color="blue">{name || val}</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: "Grey Type",
      dataIndex: "greyType",
      key: "greyType",
      width: 110,
      render: (val: string) => {
        if (!val) return <Text type="secondary">—</Text>;
        const isWs = val === "websocket";
        return <Tag color={isWs ? "purple" : "blue"}>{isWs ? "Web-Socket" : "API"}</Tag>;
      },
    },
    {
      title: "Ендпоінти / Маршрути",
      key: "counts",
      width: 170,
      render: (_: any, record: AppConfigTemplate) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {parseJsonArray(record.endpointMappings).length} ендпоінтів ·{" "}
          {parseJsonArray(record.landingRoutes).length} маршрутів
        </Text>
      ),
    },
    {
      title: "Створено",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 120,
      render: (val: string) =>
        val ? new Date(val).toLocaleDateString("uk-UA") : "—",
    },
    {
      title: "Дії",
      key: "actions",
      width: 120,
      render: (_: any, record: AppConfigTemplate) => (
        <Space>
          <Tooltip title="Редагувати назву/опис">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Видалити пресет?"
            onConfirm={() => handleDelete(record.id)}
            okText="Так"
            cancelText="Ні"
          >
            <Tooltip title="Видалити">
              <Button type="text" icon={<DeleteOutlined />} danger />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          marginBottom: 16,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Title level={2}>
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate("/apps")}
              type="text"
            />
            Пресети роутингу
          </Space>
        </Title>
        <Space>
          <Input
            placeholder="Пошук пресетів..."
            prefix={<SearchOutlined />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: 250 }}
            allowClear
          />
        </Space>
      </div>

      <Table
        dataSource={filteredPresets}
        columns={columns}
        rowKey="id"
        loading={presetsQuery.isLoading}
        pagination={{ pageSize: 20 }}
        locale={{
          emptyText: "Немає збережених пресетів. Створіть пресет на сторінці редагування апки.",
        }}
      />

      <Modal
        title="Редагувати пресет"
        open={editModalOpen}
        onCancel={() => setEditModalOpen(false)}
        onOk={handleSaveEdit}
        confirmLoading={saving}
        okText="Зберегти"
        cancelText="Скасувати"
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            name="name"
            label="Назва"
            rules={[{ required: true, message: "Введіть назву пресету" }]}
          >
            <Input placeholder="Назва пресету" />
          </Form.Item>
          <Form.Item name="description" label="Опис">
            <Input.TextArea rows={3} placeholder="Опис пресету" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
